import React, { useEffect, useRef, useState } from 'react';
import {
  Box, Card, CardContent, Grid, Typography, Chip, Button, Divider,
  Dialog, IconButton, Alert,
} from '@mui/material';
import { Edit, AddPhotoAlternate, Close, PhotoCamera } from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import api from '../../services/api';
import PageHeader from '../common/PageHeader';
import AuthImage from '../common/AuthImage';
import { compressImage } from './imageUtils';

const STATUS_LABEL = {
  new: 'New', following_up: 'Following up', sample_requested: 'Sample requested',
  sourced: 'Sourced', rejected: 'Not pursuing',
};
const STATUS_COLOR = {
  new: 'info', following_up: 'primary', sample_requested: 'secondary',
  sourced: 'success', rejected: 'default',
};

const Row = ({ label, value, mono }) => (
  value === null || value === undefined || value === '' ? null : (
    <Box sx={{ display: 'flex', gap: 1.5, py: 0.6 }}>
      <Typography variant="caption" color="text.secondary" sx={{ minWidth: 118, flexShrink: 0, pt: 0.25 }}>{label}</Typography>
      <Typography variant="body2" sx={{ fontFamily: mono ? 'monospace' : undefined, whiteSpace: 'pre-line', wordBreak: 'break-word' }}>
        {value}
      </Typography>
    </Box>
  )
);

export default function ExhibitionDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const fileRef = useRef(null);
  const [d, setD] = useState(null);
  const [lightbox, setLightbox] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const load = () => api.get(`/exhibition-products/${id}`)
    .then(r => setD(r.data.data))
    .catch(() => setError('Could not load this product'));
  useEffect(() => { load(); /* eslint-disable-line */ }, [id]);

  const addPhotos = async (fileList) => {
    const files = Array.from(fileList || []);
    if (!files.length) return;
    setBusy(true); setError('');
    try {
      const fd = new FormData();
      for (const f of files) { const s = await compressImage(f); fd.append('photos', s, s.name); }
      await api.post(`/exhibition-products/${id}/photos`, fd);
      await load();
    } catch (e) {
      setError(e.response?.data?.message || 'Could not upload photos');
    } finally { setBusy(false); }
  };

  if (!d) return <Box sx={{ p: 2 }}>{error ? <Alert severity="error">{error}</Alert> : null}</Box>;

  const price = d.price != null
    ? `${d.currency || ''} ${parseFloat(d.price).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 4 })}`.trim()
    : null;

  return (
    <Box>
      <PageHeader
        title={d.product_name || 'Untitled product'}
        subtitle={[d.supplier_name, d.booth_no && `Booth ${d.booth_no}`, d.exhibition].filter(Boolean).join(' · ') || 'Exhibition find'}
        breadcrumbs={[{ label: 'Exhibition Finds', path: '/sourcing/exhibition' }, { label: d.product_name || 'Product' }]}
        action={
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button variant="outlined" startIcon={<AddPhotoAlternate />} disabled={busy}
              onClick={() => fileRef.current?.click()}>
              {busy ? 'Uploading…' : 'Add Photos'}
            </Button>
            <Button variant="contained" startIcon={<Edit />}
              onClick={() => navigate(`/sourcing/exhibition/${id}/edit`)}>Edit</Button>
          </Box>
        }
      />
      <input ref={fileRef} hidden type="file" accept="image/*" multiple
        onChange={e => { addPhotos(e.target.files); e.target.value = ''; }} />

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

      <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
        <Chip size="small" label={STATUS_LABEL[d.status] || 'New'} color={STATUS_COLOR[d.status] || 'default'} />
        {d.interest && <Chip size="small" variant="outlined" label={`${d.interest} interest`}
          color={d.interest === 'high' ? 'error' : d.interest === 'low' ? 'default' : 'warning'} />}
        {d.category && <Chip size="small" variant="outlined" label={d.category} />}
        {d.seen_date && <Chip size="small" variant="outlined" label={`Seen ${d.seen_date}`} />}
      </Box>

      <Grid container spacing={2}>
        <Grid item xs={12} md={7}>
          <Card sx={{ mb: 2 }}>
            <CardContent>
              <Typography variant="subtitle1" gutterBottom>
                Photos {d.photos?.length ? `(${d.photos.length})` : ''}
              </Typography>
              {d.photos?.length ? (
                <Grid container spacing={1}>
                  {d.photos.map(p => (
                    <Grid item xs={6} sm={4} key={p.id}>
                      <Box onClick={() => setLightbox(p.id)} sx={{ cursor: 'zoom-in' }}>
                        <AuthImage photoId={p.id} height={140} alt={p.original_name || ''} />
                      </Box>
                    </Grid>
                  ))}
                </Grid>
              ) : (
                <Box sx={{ textAlign: 'center', py: 3, color: 'text.secondary' }}>
                  <PhotoCamera sx={{ fontSize: 34, opacity: 0.35 }} />
                  <Typography variant="body2" sx={{ mt: 1 }}>No photos yet</Typography>
                </Box>
              )}
            </CardContent>
          </Card>

          {(d.specifications || d.notes) && (
            <Card>
              <CardContent>
                {d.specifications && (<>
                  <Typography variant="subtitle2" gutterBottom>Specifications</Typography>
                  <Typography variant="body2" sx={{ whiteSpace: 'pre-line', mb: d.notes ? 2 : 0 }}>{d.specifications}</Typography>
                </>)}
                {d.notes && (<>
                  {d.specifications && <Divider sx={{ mb: 2 }} />}
                  <Typography variant="subtitle2" gutterBottom>Notes</Typography>
                  <Typography variant="body2" sx={{ whiteSpace: 'pre-line' }}>{d.notes}</Typography>
                </>)}
              </CardContent>
            </Card>
          )}
        </Grid>

        <Grid item xs={12} md={5}>
          <Card sx={{ mb: 2 }}>
            <CardContent>
              <Typography variant="subtitle1" gutterBottom>Commercials</Typography>
              {price
                ? <Typography variant="h6" fontWeight={700} sx={{ mb: 1 }}>{price}{d.unit ? ` / ${d.unit}` : ''}</Typography>
                : <Typography variant="body2" color="text.disabled" sx={{ mb: 1 }}>No price recorded</Typography>}
              <Row label="MOQ" value={d.moq} />
              <Row label="Lead time" value={d.lead_time} />
              <Row label="Model" value={d.model} />
            </CardContent>
          </Card>

          <Card>
            <CardContent>
              <Typography variant="subtitle1" gutterBottom>Supplier / Booth</Typography>
              <Row label="Company" value={d.supplier_name} />
              <Row label="Booth" value={d.booth_no} />
              <Row label="Contact" value={d.contact_person} />
              <Row label="Phone" value={d.contact_phone} mono />
              <Row label="WeChat" value={d.wechat} mono />
              <Row label="Email" value={d.contact_email} />
              <Row label="Website" value={d.website} />
              <Divider sx={{ my: 1.5 }} />
              <Row label="Exhibition" value={d.exhibition} />
              <Row label="Recorded by" value={d.created_by_name} />
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Dialog open={Boolean(lightbox)} onClose={() => setLightbox(null)} maxWidth="lg">
        <Box sx={{ position: 'relative', bgcolor: '#000' }}>
          <IconButton onClick={() => setLightbox(null)}
            sx={{ position: 'absolute', top: 6, right: 6, color: '#fff', bgcolor: 'rgba(0,0,0,.5)', zIndex: 1 }}>
            <Close />
          </IconButton>
          {lightbox && <AuthImage photoId={lightbox} height="auto" fit="contain" radius={0}
            sx={{ maxHeight: '85vh', maxWidth: '92vw' }} />}
        </Box>
      </Dialog>
    </Box>
  );
}
